import { useMutation } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { getIntl } from '../../utils/intl';
import useNotify from '../useNotify';
import urls from '../../utils/urls';
import { useCollaboratorInvitations } from './useCollaboratorInvitations';
import { appAPI } from '../../utils/api';
import { onCollaboratorInviteUpdate } from './cacheKeys';
import { useState } from 'react';

const intl = getIntl('useCollaboratorInvitation', {
    invitationSuccessfullyAccepted: 'Invitation successfully accepted',
    invitationSuccessfullyDeclined: 'Invitation successfully declined',
    invitationResponseError: 'Error responding to invitation',
});

export type UseCollaboratorInvitationArgs = {
    medicalProfileId: string;
    invitationId: string;
    onInvitationResponse?: () => void;
};

export type UseCollaboratorInvitationReturnValue = {
    invitation: APITypes.medicalProfileService.CollaboratorInviteListItem | null;
    isLoadingInvitation: boolean;
    isRespondingToInvitation: boolean;
    respondToInvitation: (response: APITypes.medicalProfileService.CollaboratorInviteResponse) => void;
};

export const useCollaboratorInvitation = (
    args: UseCollaboratorInvitationArgs,
): UseCollaboratorInvitationReturnValue => {
    const { invitations, isLoadingInvitations } = useCollaboratorInvitations({});
    const [isDelayingSuccess, setIsDelayingSuccess] = useState(false);
    const notify = useNotify();

    const { isPending: isRespondingToInvitation, mutate } = useMutation({
        mutationFn: async (response: APITypes.medicalProfileService.CollaboratorInviteResponse) => {
            setIsDelayingSuccess(true);
            await appAPI.put(urls.api.collaborators.medicalProfileInvite(args.medicalProfileId, args.invitationId), {
                body: response,
            });
        },
        onSuccess: (_response, requestArgs) => {
            // Delay success to allow eventual consistency to catch up
            setTimeout(() => {
                if (args.onInvitationResponse) {
                    args.onInvitationResponse();
                }
                notify(
                    'success',
                    intl(
                        requestArgs.inviteResponse === 'accept'
                            ? 'invitationSuccessfullyAccepted'
                            : 'invitationSuccessfullyDeclined',
                    ),
                );

                onCollaboratorInviteUpdate.resetCache({ medicalProfileId: args.medicalProfileId })();

                setIsDelayingSuccess(false);
            }, 3000);
        },
        onError: () => {
            notify('error', intl('invitationResponseError'));
        },
    });

    const invitation = invitations.find(
        (invitation) => invitation.medicalProfileCollaboratorInviteId === args.invitationId,
    );

    return {
        invitation: invitation || null,
        isLoadingInvitation: isLoadingInvitations,
        isRespondingToInvitation: isRespondingToInvitation || isDelayingSuccess,
        respondToInvitation: mutate,
    };
};
